/**
 * @page TournamentCalendarPage
 * @description 賽事行事曆 — 月曆檢視 + 賽事卡片
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { TournamentStatus } from '@pokerroom/types';
import { api } from '../../lib/api';
import { TournamentStatusBadge } from './tournamentDisplay';

interface CalendarTournament {
  id: string;
  name: string;
  status: string;
  type: string;
  buyin: string;
  prizePool: string;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  _count: { entries: number };
}

interface CalendarData {
  year: number;
  month: number;
  days: Record<string, CalendarTournament[]>;
}

// A desktop day shows this many events before offering to show all of them.
const DAY_PREVIEW = 3;

const OUTLINE =
  'inline-flex min-h-11 min-w-11 items-center justify-center border border-base-300 px-3 text-sm font-medium text-base-content hover:bg-base-300 transition-colors';

// 2024-01-01 is a Monday; the week starts on Monday.
const weekdayNames = (locale: string) =>
  Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' }),
  );

function EventLink({ event, locale }: { event: CalendarTournament; locale: string }) {
  const { t } = useTranslation('tournament');
  const time = event.scheduledAt
    ? new Date(event.scheduledAt).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;
  return (
    <Link
      to={`/tournament/${event.id}`}
      className="block min-h-11 border border-base-300 bg-base-100 px-2 py-1.5 text-xs text-base-content hover:bg-base-300 transition-colors [overflow-wrap:anywhere]"
    >
      <span className="block text-sm font-medium">
        {time && <time className="mr-1 tabular-nums">{time}</time>}
        {event.name}
      </span>
      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <TournamentStatusBadge status={event.status as TournamentStatus} />
        <span className="text-base-content/70 tabular-nums">
          {t('entryCountShort', { count: event._count.entries })}
        </span>
      </span>
    </Link>
  );
}

export default function TournamentCalendarPage() {
  const { t, i18n } = useTranslation('tournament');
  const locale = i18n.language;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [expandedDays, setExpandedDays] = useState<string[]>([]);

  const {
    data: calendar,
    isLoading,
    isError,
    refetch,
  } = useQuery<CalendarData>({
    queryKey: ['tournament-calendar', year, month],
    queryFn: () => api.get(`/tournament/calendar?year=${year}&month=${month}`),
    staleTime: 30_000,
  });

  const days = calendar?.days ?? {};

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday = 0
  const totalDays = lastDay.getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));

  const prevMonth = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else setMonth(month + 1);
  };
  const goToday = () => {
    const d = new Date();
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  // Same UTC date key as the server's grouping of events by day.
  const todayStr = now.toISOString().slice(0, 10);
  const monthLabel = new Date(year, month - 1).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
  });
  const dateKey = (day: number) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const daysWithEvents = Array.from({ length: totalDays }, (_, i) => i + 1).filter(
    (day) => (days[dateKey(day)] ?? []).length > 0,
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-base-content">{t('calendarTitle')}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={prevMonth}
            aria-label={t('calendarPrev')}
            className={OUTLINE}
          >
            <IconChevronLeft size={20} stroke={1.5} aria-hidden="true" />
          </button>
          <h2
            aria-live="polite"
            className="min-w-0 flex-1 text-center text-lg font-semibold text-base-content tabular-nums sm:min-w-[9rem] sm:flex-none"
          >
            {monthLabel}
          </h2>
          <button
            type="button"
            onClick={nextMonth}
            aria-label={t('calendarNext')}
            className={OUTLINE}
          >
            <IconChevronRight size={20} stroke={1.5} aria-hidden="true" />
          </button>
          <button type="button" onClick={goToday} className={OUTLINE}>
            {t('calendarToday')}
          </button>
        </div>
      </div>

      {isError ? (
        <div role="alert" className="py-12 text-center space-y-3">
          <p className="text-base text-base-content">{t('calendarLoadError')}</p>
          <button type="button" onClick={() => void refetch()} className={OUTLINE}>
            {t('retry')}
          </button>
        </div>
      ) : isLoading ? (
        <p role="status" className="py-12 text-center text-base-content/70">
          {t('loading')}
        </p>
      ) : (
        <>
          {/* Phone and tablet: the days that have events */}
          <div className="lg:hidden space-y-4">
            {daysWithEvents.map((day) => {
              const isToday = dateKey(day) === todayStr;
              return (
                <section
                  key={day}
                  aria-current={isToday ? 'date' : undefined}
                  className={`border p-4 ${isToday ? 'border-primary bg-primary/5' : 'border-base-300 bg-base-200'}`}
                >
                  <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-base-content">
                    {new Date(year, month - 1, day).toLocaleDateString(locale, {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short',
                    })}
                    {isToday && (
                      <span className="text-sm font-medium text-primary">{t('calendarToday')}</span>
                    )}
                  </h3>
                  <ul className="space-y-2">
                    {(days[dateKey(day)] ?? []).map((event) => (
                      <li key={event.id}>
                        <EventLink event={event} locale={locale} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
            {daysWithEvents.length === 0 && (
              <p className="border border-base-300 bg-base-200 py-8 text-center text-base-content">
                {t('calendarEmpty')}
              </p>
            )}
          </div>

          {/* Desktop: the month grid */}
          <table className="relative hidden lg:table w-full table-fixed border-collapse">
            <caption className="sr-only">{monthLabel}</caption>
            <thead>
              <tr>
                {weekdayNames(locale).map((name) => (
                  <th
                    scope="col"
                    key={name}
                    className="py-2 text-center text-sm font-semibold text-base-content/70"
                  >
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, w) => (
                <tr key={w}>
                  {week.map((day, i) => {
                    if (day === null)
                      return (
                        <td key={`e-${i}`} className="border border-base-300 bg-base-200 p-0" />
                      );
                    const isToday = dateKey(day) === todayStr;
                    const events = days[dateKey(day)] ?? [];
                    const expanded = expandedDays.includes(dateKey(day));
                    const shown = expanded ? events : events.slice(0, DAY_PREVIEW);
                    return (
                      <td
                        key={day}
                        aria-current={isToday ? 'date' : undefined}
                        className={`border p-0 align-top ${isToday ? 'border-primary bg-primary/5' : 'border-base-300 bg-base-100'}`}
                      >
                        {/* The theme fixes td height and tints hovered rows: the cell sets its own background and the content its height. */}
                        <div className="min-h-24 p-1.5">
                          <span
                            className={`mb-1 inline-flex h-6 min-w-6 items-center justify-center px-1 text-sm tabular-nums ${isToday ? 'bg-primary font-bold text-primary-content' : 'text-base-content'}`}
                          >
                            {day}
                          </span>
                          {events.length > 0 && (
                            <ul className="space-y-1">
                              {shown.map((event) => (
                                <li key={event.id}>
                                  <EventLink event={event} locale={locale} />
                                </li>
                              ))}
                            </ul>
                          )}
                          {events.length > DAY_PREVIEW && (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              onClick={() =>
                                setExpandedDays((current) =>
                                  expanded
                                    ? current.filter((key) => key !== dateKey(day))
                                    : [...current, dateKey(day)],
                                )
                              }
                              className="mt-1 inline-flex min-h-11 w-full items-center justify-center border border-base-300 px-2 text-xs font-medium text-base-content hover:bg-base-300 transition-colors"
                            >
                              {expanded
                                ? t('calendarShowLess')
                                : t('calendarShowAll', { count: events.length })}
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
